/**
 * Состояние диалога с ассистентом (US-10).
 *
 * Лента — список записей: реплика пользователя, ответ бэка, служебная
 * ошибка сервиса. Никаких заготовленных ответов: если бэк недоступен, в
 * ленту встаёт честное «сервис недоступен» (US-10, §«Состояния»).
 */
import { isAxiosError } from 'axios';
import { useCallback, useRef, useState } from 'react';

import { askAssistant } from '../../requests/assistant.request';
import type { AssistantAnswer, AssistantMode } from '../../types/assistant';

export type ChatEntry =
  | { id: string; kind: 'user'; text: string }
  | { id: string; kind: 'answer'; answer: AssistantAnswer }
  | { id: string; kind: 'error'; code: 'unavailable' | 'forbidden' };

interface UseAssistantChatOptions {
  kato: number | null;
  year: number | null;
  lang: string;
  /** Бэк может отнести свободный запрос к другому режиму (FR-2.2). */
  onModeDetected: (mode: AssistantMode) => void;
}

export interface AssistantChat {
  entries: ChatEntry[];
  pending: boolean;
  send: (text: string, mode: AssistantMode) => void;
  conversationId: string | null;
}

export const useAssistantChat = ({
  kato,
  year,
  lang,
  onModeDetected,
}: UseAssistantChatOptions): AssistantChat => {
  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [pending, setPending] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const seq = useRef(0);

  // Date.now() в id не годится: два сообщения одной миллисекунды получили бы
  // одинаковый key.
  const nextId = useCallback((prefix: string) => {
    seq.current += 1;
    return `${prefix}-${seq.current}`;
  }, []);

  const push = useCallback((entry: ChatEntry) => {
    setEntries((prev) => [...prev, entry]);
  }, []);

  const send = useCallback(
    (text: string, mode: AssistantMode) => {
      const message = text.trim();
      if (message === '' || pending) return;
      push({ id: nextId('user'), kind: 'user', text: message });
      setPending(true);
      askAssistant({
        mode,
        message,
        kato,
        year,
        conversationId,
        lang,
      })
        .then((answer) => {
          if (answer.conversationId !== null) {
            setConversationId(answer.conversationId);
          }
          if (answer.mode !== null && answer.mode !== mode) {
            onModeDetected(answer.mode);
          }
          push({ id: nextId('answer'), kind: 'answer', answer });
        })
        .catch((error: unknown) => {
          console.error('askAssistant failed', error);
          const forbidden =
            isAxiosError(error) && error.response?.status === 403;
          push({
            id: nextId('error'),
            kind: 'error',
            code: forbidden ? 'forbidden' : 'unavailable',
          });
        })
        .finally(() => setPending(false));
    },
    [pending, push, nextId, kato, year, conversationId, lang, onModeDetected],
  );


  return { entries, pending, send, conversationId };
};
